import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@mlm/ui';
import {
  CONTENT_REPORT_REASON_LABELS,
  CONTENT_REPORT_STATUSES,
  CONTENT_REPORT_TARGETS,
  type ContentReportStatus,
  type ContentReportTarget,
} from '@mlm/shared';
import { useHasPerm } from '../lib/auth-store';
import { ReviewDecisionDialog } from '../components/review-decision-dialog';
import {
  decideContentReport,
  listContentReports,
  setReportTargetHidden,
  type ContentReportRow,
} from '../lib/creators-api';

const TARGET_LABELS: Record<ContentReportTarget, string> = {
  course: 'Course',
  review: 'Review',
  question: 'Q&A post',
  announcement: 'Announcement',
};

const PAGE_SIZE = 25;

/**
 * Admin → Moderation. The queue of member-filed reports on course content.
 *
 * Two things separate it from the Feedback inbox it is modelled on. Reports
 * point at a TARGET that still exists and is still being read, so the primary
 * action is hiding that target, not filing the message; and several members
 * commonly report the same thing, which is why `openCount` is on the row —
 * five reports on one review is a different signal from one.
 */
export function ModerationPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<ContentReportStatus | 'all'>('open');
  const [targetKind, setTargetKind] = useState<ContentReportTarget | 'all'>('all');
  const [page, setPage] = useState(1);
  const [deciding, setDeciding] = useState<{
    row: ContentReportRow;
    decision: 'resolve' | 'dismiss';
  } | null>(null);
  const canAct = useHasPerm('moderation.act');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-reports', status, targetKind, page],
    queryFn: () =>
      listContentReports({
        status: status === 'all' ? undefined : status,
        targetKind: targetKind === 'all' ? undefined : targetKind,
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-reports'] });

  const decide = useMutation({
    mutationFn: (args: { id: string; decision: 'resolve' | 'dismiss'; note?: string }) =>
      decideContentReport(args.id, args.decision, args.note),
    meta: { successMessage: 'Report closed.' },
    onSuccess: () => {
      invalidate();
      setDeciding(null);
    },
  });

  const hide = useMutation({
    mutationFn: (args: { id: string; hidden: boolean }) =>
      setReportTargetHidden(args.id, args.hidden),
    meta: { successMessage: 'Target visibility updated.' },
    // Hiding a target changes `targetHidden` on EVERY report pointing at it,
    // not just this row — refetch the page rather than patch one card.
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Moderation</h1>
        <p className="text-sm text-muted-foreground">
          Content members reported on courses — reviews, Q&amp;A posts and announcements. Hiding a
          target removes it from every learner&apos;s view; resolving or dismissing only closes the
          report.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={status}
          onValueChange={(v) => {
            setStatus(v as ContentReportStatus | 'all');
            setPage(1);
          }}
        >
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            {CONTENT_REPORT_STATUSES.map((s) => (
              <TabsTrigger key={s} value={s} className="capitalize">
                {s}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select
          value={targetKind}
          onValueChange={(v) => {
            setTargetKind(v as ContentReportTarget | 'all');
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All content types</SelectItem>
            {CONTENT_REPORT_TARGETS.map((t) => (
              <SelectItem key={t} value={t}>
                {TARGET_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nothing to moderate here.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((row) => (
            <ReportCard
              key={row.id}
              row={row}
              canAct={canAct}
              hidePending={hide.isPending}
              onHide={(hidden) => hide.mutate({ id: row.id, hidden })}
              onDecide={(decision) => setDeciding({ row, decision })}
            />
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {page} · {total} report{total === 1 ? '' : 's'}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ReviewDecisionDialog
        open={!!deciding}
        onOpenChange={(o) => !o && setDeciding(null)}
        title={deciding?.decision === 'dismiss' ? 'Dismiss report' : 'Resolve report'}
        description={
          deciding?.decision === 'dismiss'
            ? 'Nothing was wrong with the content. The report closes and the target is left as it is.'
            : 'The report is closed as actioned. Hide the target first if it should come down.'
        }
        label="Note (optional)"
        placeholder="What you did, for whoever reads this queue next…"
        confirmLabel={deciding?.decision === 'dismiss' ? 'Dismiss' : 'Resolve'}
        confirmVariant={deciding?.decision === 'dismiss' ? 'destructive' : 'default'}
        pending={decide.isPending}
        onConfirm={(note) =>
          decide.mutate({
            id: deciding!.row.id,
            decision: deciding!.decision,
            note: note || undefined,
          })
        }
      />
    </div>
  );
}

function ReportCard({
  row,
  canAct,
  hidePending,
  onHide,
  onDecide,
}: {
  row: ContentReportRow;
  canAct: boolean;
  hidePending: boolean;
  onHide: (hidden: boolean) => void;
  onDecide: (decision: 'resolve' | 'dismiss') => void;
}) {
  const open = row.status === 'open';
  return (
    <Card>
      <CardContent className="space-y-2 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{TARGET_LABELS[row.targetKind]}</Badge>
          <Badge variant={open ? 'warning' : 'muted'} className="capitalize">
            {row.status}
          </Badge>
          <Badge variant="destructive">
            {CONTENT_REPORT_REASON_LABELS[row.reason] ?? row.reason}
          </Badge>
          {/* Only worth saying when it is more than this one report — "1 report"
              on every card is noise the reader learns to skip past. */}
          {row.openCount > 1 && <Badge variant="warning">{row.openCount} reports</Badge>}
          {row.targetHidden === true && <Badge variant="muted">Hidden</Badge>}
          <span className="ml-auto text-xs text-muted-foreground">
            {new Date(row.createdAt).toLocaleString()}
          </span>
        </div>

        {row.excerpt ? (
          <p className="whitespace-pre-line rounded bg-muted/40 p-2 text-sm">{row.excerpt}</p>
        ) : (
          <p className="text-sm text-muted-foreground">
            The whole course was reported — there is no single passage to quote.
          </p>
        )}
        {row.details && (
          <p className="whitespace-pre-line text-sm text-muted-foreground">
            Reporter said: {row.details}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{row.courseName}</span>
          <span>· reported by {row.reporterName ?? 'a deleted account'}</span>
        </div>
        {row.resolutionNote && (
          <p className="text-xs text-muted-foreground">Closing note: {row.resolutionNote}</p>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          {row.targetKind === 'course' ? (
            // A course has no hide switch: unpublishing is a listing decision
            // with money attached (enrolled learners, a creator's revenue), so
            // it belongs on the page that owns listings.
            <Button asChild size="sm" variant="outline">
              <Link to="/creators?tab=courses">Open in Creators → Courses</Link>
            </Button>
          ) : (
            canAct && (
              <Button
                size="sm"
                variant={row.targetHidden ? 'outline' : 'destructive'}
                disabled={hidePending}
                onClick={() => onHide(!row.targetHidden)}
              >
                {row.targetHidden ? 'Unhide target' : 'Hide target'}
              </Button>
            )
          )}
          {canAct && open && (
            <>
              <Button size="sm" onClick={() => onDecide('resolve')}>
                Resolve
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDecide('dismiss')}>
                Dismiss
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
